import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { AuthService } from '../../auth/auth.service';
import { NavDeskopComponent } from './nav-deskop/nav-deskop.component';
import { NavMobileComponent } from './nav-mobile/nav-mobile.component';

@Component({
  selector: 'app-nav',
  imports: [NavDeskopComponent, NavMobileComponent],
  templateUrl: './nav.component.html',
  styleUrl: './nav.component.scss',
})
export class NavComponent implements OnInit {
  isDesktopSize = signal(window.innerWidth >= 768);
  isLoggedIn = computed(() => this.authService.isAuthenticated());

  menuItems: NavMenuItem[] = [];
  menuMobileItems: NavMenuItem[] = [];
  isMobileMenuOpen = false;

  public locale = 'en';
  languageOptions: DropdownItem[] = [];
  isLanguageDropdownOpen = false;

  private authService = inject(AuthService);
  private breakpointObserver = inject(BreakpointObserver);
  private translate = inject(TranslateService);
  private router = inject(Router);

  constructor() {
    this.breakpointObserver
      .observe(['(min-width: 768px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((result) => {
        this.isDesktopSize.set(result.matches);
      });

    effect(() => {
      this.isLoggedIn(); // Track the signal for reactivity
      this.buildMenuItems();
    });
  }

  ngOnInit(): void {
    this.locale = this.translate.currentLang;
    this.languageOptions = LANGUAGE_OPTIONS;
  }

  private buildMenuItems(): void {
    this.menuItems = [
      // Authenticated menu items
      {
        label: this.translate.instant('dashboard'),
        route: ['/dashboard'],
        requiresLogin: true,
      },
      {
        label: this.translate.instant('companies'),
        route: ['/companies'],
        requiresLogin: true,
      },
      // Public menu items (shown when logged out)
      {
        label: this.translate.instant('about'),
        url: 'https://www.globalbattery.org/about/',
        external: true,
        requiresLoggedOut: true,
      },
      {
        label: this.translate.instant('supporters'),
        url: 'https://www.globalbattery.org/members/',
        external: true,
        requiresLoggedOut: true,
      },
      {
        label: this.translate.instant('publications'),
        url: 'https://www.globalbattery.org/publications/',
        external: true,
        requiresLoggedOut: true,
      },
    ];

    this.menuMobileItems = [
      // Add all main navigation items (preserving dropdown structure)
      ...this.menuItems.map((item) => ({
        ...item,
        // Convert options to mobile dropdown format if they exist
        ...(item.options
          ? {
              dropdown: true,
              dropdownItems: item.options.map((option) => ({
                label: option.label,
                click: () => {
                  if (option.route && !option.disabled) {
                    this.router.navigate(option.route);
                  }
                },
                value: option.value,
              })),
            }
          : {}),
      })),
      {
        label: this.translate.instant('languages'),
        showAlways: true,
        dropdown: true,
        dropdownItems: this.languageOptions.map((lang) => ({
          label: lang.label,
          click: () => this.setTransLanguage(lang.value),
          svgUrl: lang.svgUrl,
        })),
      },
      {
        label: this.translate.instant('logout'),
        click: () => this.logout(),
        requiresLogin: true,
      },
      {
        label: this.translate.instant('login'),
        click: () => this.login(),
        requiresLoggedOut: true,
      },
    ];
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  login() {
    this.router.navigate(['/login']);
  }

  logout() {
    this.authService.logout();
  }

  setTransLanguage(selectedLang: string) {
    this.translate.use(selectedLang);
  }
}

export interface NavMenuItem {
  label: string;
  value?: string; // For dropdown option identification
  icon?: string;
  iconName?: string; // For nested options in desktop view
  svgUrl?: string; // For flag icons in language selector
  route?: string[];
  external?: boolean;
  url?: string;
  // Visibility rules - use one of these:
  requiresLogin?: boolean; // Only show when logged in
  requiresLoggedOut?: boolean; // Only show when logged out
  showAlways?: boolean; // Show regardless of auth state (default: false)
  options?: NavMenuItem[]; // For desktop dropdown menus
  disabled?: boolean;
  click?: () => void; // For mobile click handlers
  dropdown?: boolean; // For mobile dropdown detection
  dropdownItems?: { label: string; click: () => void; value?: string; svgUrl?: string }[]; // For mobile dropdown items
}

export interface DropdownItem {
  value: string;
  label: string;
  svgUrl?: string;
  iconName?: string;
  route?: string[];
  click?: () => void;
  // Extended properties for custom templates
  data?: Record<string, unknown>; // For passing additional data like company object
}

export const LANGUAGE_OPTIONS: DropdownItem[] = [
  { value: 'en', label: 'English', svgUrl: 'https://api.intracen.org/flags/826.svg' },
  { value: 'es', label: 'Español', svgUrl: 'https://api.intracen.org/flags/724.svg' },
  { value: 'fr', label: 'Français', svgUrl: 'https://api.intracen.org/flags/251.svg' },
];

// Utility function to check if a nav menu item should be shown based on auth state
export function shouldShowNavItem(item: NavMenuItem, isAuthenticated: boolean): boolean {
  // Check visibility rules
  if (item.requiresLogin && !isAuthenticated) {
    return false;
  }
  if (item.requiresLoggedOut && isAuthenticated) {
    return false;
  }
  if (item.showAlways) {
    return true;
  }

  // Default behavior: show if no specific rule is set
  return !!(item.requiresLogin || item.requiresLoggedOut || item.showAlways);
}
